import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { 
  Settings, 
  RefreshCw, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Loader2,
  Languages,
  AlertTriangle,
  Play,
  RotateCcw
} from 'lucide-react';
import { AdminPageWrapper } from '../components/admin/AdminPageWrapper';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { cn } from '../lib/utils';
import { useAdminTheme } from '../contexts/AdminThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status icons et couleurs
const STATUS_CONFIG = {
  pending: { icon: Clock, color: 'text-yellow-500', bg: 'bg-yellow-500/10', label: '⏳ En attente' },
  processing: { icon: Loader2, color: 'text-blue-500', bg: 'bg-blue-500/10', label: '🔄 En cours', animate: true },
  completed: { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-500/10', label: '✅ Complété' },
  failed: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-500/10', label: '❌ Échoué' }
};

export function AdminTranslationsPage() {
  const { t } = useTranslation();
  const { isLight } = useAdminTheme();
  const [settings, setSettings] = useState(null);
  const [queue, setQueue] = useState({ stats: {}, tasks: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [token, setToken] = useState('');

  // Récupérer le token admin (simplifié pour la démo)
  useEffect(() => {
    const savedToken = localStorage.getItem('admin_token');
    if (savedToken) {
      setToken(savedToken);
    }
  }, []);

  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  // Charger les données
  const loadData = async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      const [settingsRes, queueRes] = await Promise.all([
        axios.get(`${API_URL}/api/translations/settings`, { headers: authHeaders }),
        axios.get(`${API_URL}/api/translations/queue?limit=20`, { headers: authHeaders })
      ]);
      
      setSettings(settingsRes.data);
      setQueue(queueRes.data);
      setError(null);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      loadData();
    }
  }, [token]);

  // Toggle auto-translate
  const handleToggleAuto = async (enabled) => {
    try {
      setActionLoading(true);
      await axios.post(
        `${API_URL}/api/translations/settings/toggle-auto?enabled=${enabled}`,
        {},
        { headers: authHeaders }
      );
      setSettings(prev => ({ ...prev, auto_translate_enabled: enabled }));
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Retry failed translations
  const handleRetryFailed = async () => {
    try {
      setActionLoading(true);
      const res = await axios.post(
        `${API_URL}/api/translations/queue/retry`,
        {},
        { headers: authHeaders }
      );
      alert(`${res.data.retried_count} traduction(s) relancée(s)`);
      loadData();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Process a specific task
  const handleProcessTask = async (taskId) => {
    try {
      await axios.post(
        `${API_URL}/api/translations/queue/process/${taskId}`,
        {},
        { headers: authHeaders }
      );
      loadData();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  // Login form si pas de token
  if (!token) {
    return (
      <AdminPageWrapper title="Traductions" subtitle="Connectez-vous pour accéder">
        <div className={cn(
          "rounded-lg p-8 border max-w-md mx-auto",
          isLight ? "bg-white border-gray-200" : "bg-gray-900 border-gray-800"
        )}>
          <h1 className={cn("text-2xl font-bold mb-6", isLight ? "text-gray-900" : "text-white")}>
            Admin - Traductions
          </h1>
          <p className={cn("mb-4", isLight ? "text-gray-600" : "text-gray-200")}>
            Connectez-vous en tant qu'admin pour accéder à cette page.
          </p>
          <AdminLoginForm onLogin={(t) => {
            setToken(t);
            localStorage.setItem('admin_token', t);
          }} />
        </div>
      </AdminPageWrapper>
    );
  }

  return (
    <AdminPageWrapper title="Système de Traduction" subtitle="Gestion des traductions automatiques">
      <div className="max-w-7xl mx-auto" data-testid="admin-translations-page">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Languages className="h-8 w-8 text-mutagim-red" />
            <h1 className={cn("text-2xl md:text-3xl font-bold", isLight ? "text-gray-900" : "text-white")}>
              Système de Traduction
            </h1>
          </div>
          <Button
            variant="outline"
            onClick={loadData}
            disabled={loading}
            className={isLight ? "border-gray-300 text-gray-700" : "border-gray-700 text-white"}
          >
            <RefreshCw className={cn("h-4 w-4 me-2", loading && "animate-spin")} />
            Actualiser
          </Button>
        </div>

        {error && (
          <div className="bg-red-900/30 border border-red-500 rounded-lg p-4 mb-6 text-red-300">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-mutagim-red" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Paramètres */}
            <div className="lg:col-span-1 space-y-6">
              {/* Switch global */}
              <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
                <div className="flex items-center gap-3 mb-4">
                  <Settings className="h-5 w-5 text-mutagim-red" />
                  <h2 className="text-lg font-semibold text-white">Paramètres</h2>
                </div>

                <div className="space-y-4">
                  {/* Auto-translate toggle */}
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-white font-medium">Mode Auto</Label>
                      <p className="text-gray-200 text-sm">
                        Traduction automatique de tout nouveau contenu
                      </p>
                    </div>
                    <Switch
                      checked={settings?.auto_translate_enabled || false}
                      onCheckedChange={handleToggleAuto}
                      disabled={actionLoading}
                      className="data-[state=checked]:bg-mutagim-red"
                      data-testid="auto-translate-switch"
                    />
                  </div>

                  {/* Indicateur de statut */}
                  <div className={cn(
                    "p-3 rounded-lg",
                    settings?.auto_translate_enabled ? "bg-green-500/10" : "bg-gray-800"
                  )}>
                    <p className={cn(
                      "text-sm font-medium",
                      settings?.auto_translate_enabled ? "text-green-500" : "text-gray-200"
                    )}>
                      {settings?.auto_translate_enabled 
                        ? "✅ Traduction automatique ACTIVÉE"
                        : "⏸️ Traduction automatique désactivée"
                      }
                    </p>
                  </div>

                  {/* Entités auto-traduites */}
                  {settings?.auto_translate_entities && (
                    <div>
                      <Label className="text-gray-200 text-sm">Entités auto-traduites :</Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {settings.auto_translate_entities.map(entity => (
                          <span 
                            key={entity}
                            className="px-2 py-1 bg-gray-800 text-white text-xs rounded"
                          >
                            {entity}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Statistiques */}
              <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
                <h2 className="text-lg font-semibold text-white mb-4">Statistiques</h2>
                <div className="grid grid-cols-2 gap-4">
                  {Object.entries(queue.stats || {}).map(([status, count]) => {
                    const config = STATUS_CONFIG[status];
                    const Icon = config?.icon || Clock;
                    return (
                      <div 
                        key={status}
                        className={cn("p-3 rounded-lg", config?.bg || "bg-gray-800")}
                      >
                        <div className="flex items-center gap-2">
                          <Icon className={cn("h-4 w-4", config?.color, config?.animate && "animate-spin")} />
                          <span className="text-white font-bold">{count}</span>
                        </div>
                        <p className="text-gray-200 text-xs mt-1 capitalize">{status}</p>
                      </div>
                    );
                  })}
                </div>

                {/* Actions */}
                <div className="mt-4 pt-4 border-t border-gray-800">
                  <Button
                    variant="outline"
                    onClick={handleRetryFailed}
                    disabled={actionLoading || (queue.stats?.failed || 0) === 0}
                    className="w-full border-gray-700 text-white"
                  >
                    <RotateCcw className="h-4 w-4 me-2" />
                    Relancer les échecs ({queue.stats?.failed || 0})
                  </Button>
                </div>
              </div>
            </div>

            {/* File d'attente */}
            <div className="lg:col-span-2">
              <div className="bg-gray-900 rounded-lg border border-gray-800">
                <div className="p-4 border-b border-gray-800">
                  <h2 className="text-lg font-semibold text-white">
                    File d'attente ({queue.total || 0} tâches)
                  </h2>
                </div>

                <div className="divide-y divide-gray-800 max-h-[600px] overflow-y-auto">
                  {(queue.tasks || []).length === 0 ? (
                    <div className="p-8 text-center text-gray-200">
                      Aucune traduction en file d'attente
                    </div>
                  ) : (
                    queue.tasks.map((task) => {
                      const config = STATUS_CONFIG[task.status] || STATUS_CONFIG.pending;
                      const Icon = config.icon;
                      
                      return (
                        <div 
                          key={task.id}
                          className="p-4 hover:bg-gray-800/50 transition-colors"
                          data-testid={`queue-item-${task.id}`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className={cn(
                                  "px-2 py-0.5 rounded text-xs font-medium",
                                  config.bg, config.color
                                )}>
                                  <Icon className={cn("inline h-3 w-3 me-1", config.animate && "animate-spin")} />
                                  {task.status}
                                </span>
                                <span className="text-gray-500 text-xs">
                                  {task.entity_type}.{task.field_name}
                                </span>
                                {task.is_legal && (
                                  <span className="flex items-center gap-1 text-yellow-500 text-xs">
                                    <AlertTriangle className="h-3 w-3" />
                                    Légal
                                  </span>
                                )}
                              </div>
                              <p className="text-white text-sm truncate">
                                {task.source_text}
                              </p>
                              {task.error && (
                                <p className="text-red-400 text-xs mt-1">
                                  Erreur: {task.error}
                                </p>
                              )}
                              <p className="text-gray-500 text-xs mt-1">
                                {new Date(task.created_at).toLocaleString()}
                              </p>
                            </div>
                            
                            {(task.status === 'pending' || task.status === 'failed') && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleProcessTask(task.id)}
                                className="text-mutagim-red hover:bg-mutagim-red/10"
                              >
                                <Play className="h-4 w-4" />
                              </Button>
                            )}
                          </div>

                          {/* Traductions complétées */}
                          {task.status === 'completed' && task.translations && (
                            <div className="mt-2 grid grid-cols-3 gap-2">
                              {Object.entries(task.translations).map(([lang, text]) => (
                                <div key={lang} className="bg-gray-800 p-2 rounded text-xs">
                                  <span className="text-gray-200 uppercase">{lang}:</span>
                                  <p className="text-white truncate">{text}</p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminPageWrapper>
  );
}

// Composant de login admin simplifié
function AdminLoginForm({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const res = await axios.post(`${API_URL}/api/auth/login`, { email, password });
      // Le token est dans access_token
      const token = res.data.access_token || res.data.token;
      const role = res.data.role;
      
      if (role === 'admin' && token) {
        onLogin(token);
      } else {
        setError('Accès admin requis');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-900/30 border border-red-500 rounded p-3 text-red-300 text-sm">
          {error}
        </div>
      )}
      <div>
        <Label className="text-white">Email admin</Label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full mt-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white"
          placeholder="admin@mutagim.shop"
          required
        />
      </div>
      <div>
        <Label className="text-white">Mot de passe</Label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full mt-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white"
          required
        />
      </div>
      <Button 
        type="submit" 
        disabled={loading}
        className="w-full bg-mutagim-red hover:bg-red-700"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Se connecter'}
      </Button>
    </form>
  );
}

export default AdminTranslationsPage;
